/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Col, Empty, Pagination, Row } from 'antd';
import { ColProps } from 'antd/lib/grid';
import classnames from 'classnames';
import CircularProgress from 'components/CircularProgress';
import { keyBy } from 'lodash';
import React, { useCallback } from 'react';

import { EmptyDataContainerStyled } from './ResponsiveMobileTable';
import { DataEntity, IMobileTableConfig, ITableProps, TitleKey } from './types';

function isKeyOf<T>(key: TitleKey<T> | keyof T): key is keyof T {
  if (typeof key === 'string') return true;
  return false;
}

const MobileTable = <T extends DataEntity>(props: ITableProps<T>) => {
  const {
    dataSource,
    meta,
    columns,
    loading,
    onRow,
    pagination,
    nullRender,
    mobileConfig,
    className = '',
    onChange,
  } = props;
  const { grid, titleKeys, onClickTitle } = mobileConfig as IMobileTableConfig<T>;
  const isNoData = !loading && (meta?.itemCount === 0 || !dataSource);
  const indexedColumns = React.useMemo(() => keyBy(columns, 'dataIndex'), [columns]);

  const renderColumn = (row: T, column: keyof T | TitleKey<T>, gridRowIndex: number, isShowTitle = true) => {
    let key: keyof T;
    let colProps: ColProps = {};

    if (isKeyOf(column)) {
      key = column;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      ({ value: key, ...colProps } = column);
    }

    colProps = {
      span: 8, // default span to 8, to remove set it undefined where used
      ...colProps,
    };

    const indexedColumn = indexedColumns[key as keyof typeof indexedColumns] as any;

    const title = indexedColumn?.title;
    let value = indexedColumn?.render?.(row[key], row);
    if (value === undefined) value = row[key];
    if (value === undefined) value = nullRender;

    return (
      <Col style={{ overflow: 'hidden' }} key={`grid-row-${gridRowIndex}-${key}` as string} {...colProps}>
        {isShowTitle && <div className='title'>{title}</div>}
        <div className='value'>{value}</div>
      </Col>
    );
  };

  const onChangePagination = useCallback(
    (page: number) => {
      onChange?.(
        {
          current: page,
        },
        null as any,
        null as any,
        {
          action: 'paginate',
          currentDataSource: dataSource as T[],
        },
      );
    },
    [onChange],
  );

  const onClickRow = (row: T) => (e: React.MouseEvent<HTMLDivElement>) => {
    onRow?.(row)?.onClick?.(e);
  };

  return (
    <div className={`mobile-table__wrapper ${className}`}>
      {isNoData ? (
        <EmptyDataContainerStyled className='gx-p-2 gx-responsive-table'>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </EmptyDataContainerStyled>
      ) : (
        <>
          <div className='data__wrapper'>
            <div className={classnames('gx-loader-view', { show: loading })}>
              <CircularProgress />
            </div>
            {dataSource?.map((row, index) => (
              <div
                className='box-gradient box-gradient-simple'
                key={row.key || `row-${index}`}
                onClick={onClickRow(row)}>
                <div className=''>
                  {titleKeys && (
                    <Row className='card-title' onClick={onClickTitle?.(row) || undefined}>
                      {titleKeys.map((key) => renderColumn(row, key, 0, false))}
                    </Row>
                  )}
                  {grid.map((gridRow, gridRowIndex) => (
                    <Row className='grid-row' key={`grid-row-${gridRowIndex}` as string}>
                      {gridRow.map((column) => renderColumn(row, column, gridRowIndex))}
                    </Row>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {pagination && <Pagination {...pagination} onChange={onChangePagination} />}
        </>
      )}
    </div>
  );
};

export default MobileTable;
